import qz from 'qz-tray';
import jwtAuthAxios from './auth/jwtAuth';

let connecting = null;
let firmaConfigurada = null; // null = sin intentar, true = firmando, false = modo anónimo

// Firma de las peticiones. Sin esto QZ Tray muestra "An anonymous request wants
// to access connected printers" en cada impresión, y no deja recordar el Allow:
// para sitios sin firmar desactiva esa opción a propósito.
//
// Si el backend todavía no tiene certificado cargado, se sigue en modo anónimo
// (con el cartel) en vez de romper la impresión.
const configurarFirma = async () => {
    if (firmaConfigurada !== null) return firmaConfigurada;

    try {
        const { data: certificado } = await jwtAuthAxios.get('/qz/certificate', { responseType: 'text' });
        if (!certificado || !String(certificado).includes('BEGIN CERTIFICATE')) throw new Error('certificado vacío');

        qz.security.setCertificatePromise((resolve) => resolve(certificado));
        qz.security.setSignatureAlgorithm('SHA512');
        qz.security.setSignaturePromise((porFirmar) => (resolve, reject) => {
            jwtAuthAxios.post('/qz/sign', { request: porFirmar }, { responseType: 'text' })
                .then(({ data }) => resolve(data))
                .catch(reject);
        });
        firmaConfigurada = true;
    } catch (err) {
        console.warn('[QZ] sin certificado configurado, se imprime en modo anónimo:', err?.message || err);
        firmaConfigurada = false;
    }
    return firmaConfigurada;
};

const ensureConnected = async () => {
    if (qz.websocket.isActive()) return;

    // Tiene que quedar configurada ANTES de conectar: QZ firma el handshake.
    await configurarFirma();

    if (!connecting) {
        connecting = qz.websocket.connect()
            .catch((err) => {
                connecting = null;
                throw err;
            });
    }
    await connecting;
    connecting = null;
};

const qzPrintServices = {};

qzPrintServices.isAvailable = async () => {
    try {
        await ensureConnected();
        return true;
    } catch (err) {
        return false;
    }
};

qzPrintServices.listLocalPrinters = async () => {
    await ensureConnected();
    const list = await qz.printers.find();
    return Array.isArray(list) ? list : [list];
};

qzPrintServices.printZpl = async ({ printerName, host, port, zpl }) => {
    if (!zpl) throw new Error('ZPL vacío');
    if (!printerName && !host) throw new Error('Falta printerName o host:port');

    await ensureConnected();
    const target = host
        ? { host, port: port || 9100 }
        : printerName;
    const config = qz.configs.create(target);
    console.log('[QZ] Printing to', target, '— ZPL size:', zpl.length, 'preview:', zpl.slice(0, 120));
    await qz.print(config, [{ type: 'raw', format: 'command', data: zpl, options: { language: 'zpl' } }]);
};

// Manda ~HS (host status) a la impresora. Es una consulta de estado: no imprime
// ni mueve papel, así que sirve para saber si responde sin gastar una etiqueta.
// Si el equipo no está en esa IP, QZ no logra abrir el socket y esto revienta.
qzPrintServices.probe = async ({ printerName, host, port }) => {
    await ensureConnected();

    if (!printerName && host) {
        const config = qz.configs.create({ host, port: port || 9100 });
        await qz.print(config, [{ type: 'raw', format: 'command', data: '~HS\r\n', options: { language: 'zpl' } }]);
        return { ok: true, via: `${host}:${port || 9100}` };
    }

    // Por nombre no hay socket que probar: alcanza con que el PC la tenga instalada.
    const encontradas = await qzPrintServices.listLocalPrinters();
    const existe = encontradas.some((p) => String(p) === String(printerName));
    if (!existe) throw new Error(`El PC no tiene ninguna impresora llamada "${printerName}"`);
    return { ok: true, via: printerName };
};

qzPrintServices.disconnect = async () => {
    if (qz.websocket.isActive()) {
        await qz.websocket.disconnect();
    }
};

export default qzPrintServices;
